import { memo, useEffect } from 'react'
import { colors } from 'theme/colors'
import { Stack, Text } from 'ui/baseComponents'
import ReportSection from 'ui/clinicianScreens/Patient/Report/components/ReportSection'
import ReportTableActivityCategories from 'ui/clinicianScreens/Patient/Report/sections/Activity/ReportTableActivityCategories'
import ReportChartActivityCategories from 'ui/clinicianScreens/Patient/Report/sections/Activity/ReportChartActivityCategories'
import ReportMetric from 'ui/clinicianScreens/Patient/Report/components/ReportMetric'
import ReportTableActivities from 'ui/clinicianScreens/Patient/Report/sections/Activity/ReportTableActivities'
import ReportTitle from 'ui/clinicianScreens/Patient/Report/components/ReportTitle'
import EditableLLMContent from 'ui/clinicianScreens/Patient/Report/components/EditableLLMContent'
import useActivityOverview from 'ui/hooks/useActivityOverview'
import {
  getActivityTableHelper,
  getActivityDurationsByCategory,
  getWeeklyActivityAverages,
} from 'ui/clinicianScreens/Patient/Report/sections/Activity/helpers'
import { SECTION_TITLES } from 'ui/clinicianScreens/Patient/Report/consts'
import {
  ACTIVITY_COLORS,
  ActivityTableHelperDefault,
  ActivityTableHelperFewCategories,
  ActivityTableHelperNoHr,
  ActivityTableHelperNoRpe,
  ActivityTimeInCategoryHelper,
} from 'ui/clinicianScreens/Patient/Report/sections/Activity/consts'
import activityIconDuration from 'assets/img/activity-duration.svg'
import activityIconHR from 'assets/img/activity-hr.svg'
import { useSectionTitle } from 'ui/clinicianScreens/Patient/Report/sectionTitles'
import { useReportTranslation } from 'utilities/i18n'

const ACTIVITY_TABLE_HELPERS = {
  noHr: ActivityTableHelperNoHr,
  fewCategories: ActivityTableHelperFewCategories,
  noRpe: ActivityTableHelperNoRpe,
  default: ActivityTableHelperDefault,
}

const ReportSectionActivityHR = ({
  onLoadStart = () => {},
  onLoadEnd = () => {},
  hiddenSections,
  llmKey,
  llmSummary,
  onUpdate = () => {},
  addToUnsavedChanges = () => {},
  removeFromUnsavedChanges = () => {},
  startDate,
  endDate,
  prevStartDate,
  prevEndDate,
  addToHasDataSet,
  removeFromHasDataSet,
  isClinicianReport,
  hasDataSet,
}) => {
  const STYLES = {
    weeklyAvgValues: {
      paddingLeft: '34px',
    },
    highIntensityDot: {
      '&:before': {
        content: '""',
        display: 'inline-block',
        width: '0.75rem',
        height: '0.75rem',
        borderRadius: '50%',
        backgroundColor: ACTIVITY_COLORS.highIntensityHR,
        marginRight: '0.5rem',
        verticalAlign: 'middle',
      },
    },
    lowIntensityDot: {
      '&:before': {
        content: '""',
        display: 'inline-block',
        width: '0.75rem',
        height: '0.75rem',
        borderRadius: '50%',
        backgroundColor: ACTIVITY_COLORS.lowIntensityHR,
        marginRight: '0.5rem',
        verticalAlign: 'middle',
      },
    },
    tableContent: {
      padding: '0.5rem 1.5rem',
      margin: 0,
    },
    table: {
      '& .activity-name:before': {
        content: '""',
        display: 'inline-block',
        width: '16px',
        height: '16px',
        borderRadius: '50%',
        backgroundColor: colors.COOL[500],
        marginRight: '0.5rem',
        verticalAlign: 'middle',
      },
    },
  }

  const title = SECTION_TITLES.ACTIVITY
  const sectionTitle = useSectionTitle()
  const { t } = useReportTranslation()

  const { data: recentData, loading: loadingRecentActivity } =
    useActivityOverview({ startDate, endDate })
  const { data: prevData, loading: loadingPrevActivity } = useActivityOverview({
    startDate: prevStartDate,
    endDate: prevEndDate,
  })

  const loading = loadingRecentActivity || loadingPrevActivity

  useEffect(() => {
    if (loading) {
      onLoadStart(title)
    } else {
      onLoadEnd(title)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading])

  const hasData = recentData?.activity_overview?.length > 0

  useEffect(() => {
    if (recentData) {
      hasData ? addToHasDataSet(title) : removeFromHasDataSet(title)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasData])

  const {
    weeklyDurationAvg,
    weeklyDurationDiff,
    weeklyHighIntensityAvg,
    weeklyHighIntensityDiff,
  } = getWeeklyActivityAverages(recentData, prevData)

  const recentCategoryDistribution = recentData?.category_distribution || []
  const prevCategoryDistribution = prevData?.category_distribution || []

  const activityDurationsByCategory = getActivityDurationsByCategory(
    recentCategoryDistribution,
    prevCategoryDistribution,
    t,
  )

  const ActivityTableHelper =
    ACTIVITY_TABLE_HELPERS[
      getActivityTableHelper(recentData, activityDurationsByCategory)
    ]

  const hasRecentCategoryData =
    recentCategoryDistribution.reduce(
      (acc, category) => acc + category.count,
      0,
    ) > 0

  const WeeklyAverageTitle = ({
    title,
    color,
    icon,
    value,
    diff,
    ...props
  }) => (
    <Stack spacing={0} {...props}>
      <Stack direction="row" alignItems="center">
        <img src={icon} alt={`${title} icon`} width="26" />
        <Text variant="body16B" component="h3" color={color}>
          {title} <Text variant="body16">{t('report.activity.avgWeek')}</Text>
        </Text>
      </Stack>
      <ReportMetric
        {...{
          value: `${value}m`,
          trend: diff,
          variant: null,
          trendLabel: 'm',
          iconVariant: diff > 0 ? 'better' : 'worse',
          sx: STYLES.weeklyAvgValues,
        }}
      />
    </Stack>
  )

  return (
    <ReportSection
      {...{
        title: sectionTitle(title),
        sectionId: title,
        subtitle: t('report.subtitle.activity'),
        hidden:
          hiddenSections.has(title) ||
          (isClinicianReport && !hasDataSet.has(title)),
      }}
    >
      {llmSummary && (
        <EditableLLMContent
          {...{
            llmKey,
            llmSummary,
            onUpdate,
            addToUnsavedChanges,
            removeFromUnsavedChanges,
          }}
        >
          {llmSummary}
        </EditableLLMContent>
      )}

      {hasData && (
        <>
          <ReportTitle
            justifyContent="center"
            spacing={8}
            data-cy="activity-averages"
          >
            <WeeklyAverageTitle
              {...{
                title: t('report.activity.weeklyActivity'),
                color: ACTIVITY_COLORS.time,
                icon: activityIconDuration,
                value: weeklyDurationAvg,
                diff: weeklyDurationDiff,
                'data-cy': 'activity-average-duration',
              }}
            />
            <WeeklyAverageTitle
              {...{
                title: t('report.activity.hrZone3Min'),
                color: ACTIVITY_COLORS.highIntensityHR,
                icon: activityIconHR,
                value: weeklyHighIntensityAvg,
                diff: weeklyHighIntensityDiff,
                'data-cy': 'activity-average-high-intensity',
              }}
            />
          </ReportTitle>

          {hasRecentCategoryData ? (
            <>
              <ActivityTimeInCategoryHelper />
              <Stack direction="row" justifyContent="space-between" spacing={4}>
                <Stack sx={{ flex: 1 }}>
                  <ReportChartActivityCategories
                    {...{
                      activityDurationsByCategory,
                    }}
                  />
                </Stack>
                <Stack sx={{ flex: 1 }}>
                  <ReportTableActivityCategories
                    {...{ activityDurationsByCategory }}
                  />
                </Stack>
              </Stack>
            </>
          ) : (
            <ReportChartActivityCategories />
          )}

          <ReportTitle>
            <Text variant="body16B" component="p" color={ACTIVITY_COLORS.time}>
              {t('report.activity.activityAndHrZone3')}
            </Text>
            <Stack direction="row" justifyContent="space-between" spacing={2}>
              <Text
                variant="body14"
                component="p"
                sx={{ ...STYLES.highIntensityDot }}
              >
                {t('report.activity.inZone3')}
              </Text>
              <Text
                variant="body14"
                component="p"
                sx={{ ...STYLES.lowIntensityDot }}
              >
                {t('report.activity.belowZone3')}
              </Text>
            </Stack>
          </ReportTitle>

          <ReportTableActivities {...{ recentData, prevData }} />

          <div data-cy="activity-table-helper">
            <ActivityTableHelper />
          </div>
        </>
      )}

      {!hasData && !llmSummary && (
        <Text variant="body16" component="p">
          {t('report.common.noDataRecent')}
        </Text>
      )}
    </ReportSection>
  )
}

export default memo(ReportSectionActivityHR)
